import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  homeFor, spotFor, coverPan, homeLight, moodFor, MOODS, moonBox, skyLayers, starField,
  weatherLook, rainField, STORM_STAND_IN,
} from '../../utils/portedHomes';
import { moonInfo, showerOn, SYNODIC, ensureHandFont } from '../../utils/portedPlace';
import { lightsFor, lightsLabel } from '../../utils/portedPictures';

// 🏡 The painted home (PORTED_HOME_HANDOFF_2026_09_25 §6), the stage once the
// resident has CHOSEN a home (W2a). A port of the v9 mockup's layers: the day
// painting with the night one over it by the owner's hour, the dusk/dawn wash
// (soft-light), the window's tint (multiply, window only, never where the
// whole wall is glass), the lamp pools (screen), the mood light under him and
// his medallion at a spot in the room.
// W2d (§0-ter #5): the window SKY (clouds by day, stars at night, the REAL
// moon from the house's almanac math, shooting stars on shower nights), the
// CORK BOARD with the newest made things (tap = a closer look), the 📓 on its
// spot (the owner's: it opens his journal's Today page) and a tap on him says
// why the light is that color. Weather (storm takes, rain) is W4. Nothing here
// moves "just because": the clouds drift and the stars twinkle like a real
// sky, and a reduced-motion device gets a still room.
// W2c: the owner's pinned lines of his hang on the board's reserved slots as
// cream slips with a BLUE pin head (the owner's ink; the red ones are made
// things), and his words open in his hand. While he writes, the owner (only:
// the page passes `thinking` for the owner alone) sees the icons of what went
// into the reply appear one by one in the ink bubble, and a screen reader
// hears the words behind them.
//
// W4 (§6 "Weather"): the day's REAL weather (status `weather`, the house's
// almanac on the owner's day) on his glass: by day a storm or steady rain
// swaps in the home's painted storm take (a CSS stand-in until it loads),
// night beats weather (the night painting stays, the rain runs down dark
// glass); drops fall inside the window only, a storm adds lightning every
// 5-11 s (one flash, never a strobe), fog hazes the glass, snow falls, wind
// sends a few leaves past. Nothing shows through it (no stars, no moon). A
// reduced-motion device gets the still look: no drops, no flashes.
// Wave 2b (ruling 31): HEAT draws too, a warm haze on the glass (`wx.heat`;
// the sky still shows through it). And the door's one fixed line in HIS hand:
// after the owner KNOCKS and walks in, "there you are." by his medallion for
// a few seconds (`greet`; the stage sets it on the knock path only, never on
// "just slip in"). It is one of the two lines allowed in his hand that are
// not his real words (the other is the task chip's "on it.").
// W3: 🌙 goodbye: the owner waved, the lamps drop to a night-light (one small
// warm pool stays by him) · 📄 a note on the desk when a task he finished
// while the owner was away is waiting (owner only; it opens the Tasks page).
//
// The stage takes any box (the laptop stage shrinks as you chat; a phone
// card is 16:11): coverPan fits it and pans so HIS spot stays in frame (the
// choice scene's zoom lands on this exact crop, then this fades in over it).
// Class names avoid every substring the generated themes target, and the
// buttons carry the theme shield (data-prop-ui) plus their own armor.
const CSS = `
.phm, .phm * { box-sizing: border-box; }
.phm { position: absolute; inset: 0; overflow: hidden; transition: opacity .7s ease, background-color 1.1s ease; }
.phm-box { position: absolute; left: 0; top: 0; will-change: transform; }
.phm-box.phm-glide { transition: transform 1.4s cubic-bezier(.45,.05,.2,1); }
.phm-paint { position: absolute; inset: 0; width: 100%; height: 100%; display: block; user-select: none; -webkit-user-drag: none; transition: opacity 1.1s ease; }
.phm-win { position: absolute; overflow: hidden; pointer-events: none; }
.phm-win > div { position: absolute; inset: 0; transition: opacity 1.1s; }
.phm-tint { mix-blend-mode: multiply; }
.phm-clouds span { position: absolute; border-radius: 50%; background: rgba(255,255,255,.8); filter: blur(2px); animation: phmDrift linear infinite; }
@keyframes phmDrift { from { transform: translateX(-130%); } to { transform: translateX(420%); } }
.phm-stars i { position: absolute; width: 2px; height: 2px; border-radius: 50%; background: #fff; box-shadow: 0 0 4px 1px rgba(255,255,255,.7); animation: phmTwinkle 3.4s ease-in-out infinite; }
@keyframes phmTwinkle { 0%, 100% { opacity: .3; } 50% { opacity: 1; } }
.phm-moon { position: absolute; transform: translate(-50%,-50%); overflow: visible; filter: drop-shadow(0 0 6px rgba(255,242,210,.8)); }
.phm-moon.phm-harvest { filter: drop-shadow(0 0 8px rgba(255,190,110,.9)) sepia(.55) saturate(1.8); }
.phm-meteor { position: absolute; width: 28%; height: 1.5px; background: linear-gradient(90deg, #fff, rgba(255,255,255,0)); opacity: 0; transform-origin: 0 50%; }
.phm-meteor.phm-go { animation: phmMeteor 1s ease-out forwards; }
@keyframes phmMeteor { 0% { opacity: 0; transform: rotate(-28deg) translateX(0); } 12% { opacity: 1; } 100% { opacity: 0; transform: rotate(-28deg) translateX(-150%); } }
.phm-wash { position: absolute; inset: 0; mix-blend-mode: soft-light; pointer-events: none; transition: opacity 1.1s, background-color 1.1s; }
.phm-lamp { position: absolute; transform: translate(-50%,-50%); border-radius: 50%; background: radial-gradient(closest-side, rgba(255,196,120,.62), rgba(255,170,90,.2) 55%, rgba(255,160,80,0)); mix-blend-mode: screen; pointer-events: none; transition: opacity 1.2s; }
.phm-pool { position: absolute; transform: translate(-50%,-50%); width: 19%; height: 46%; border-radius: 50%; mix-blend-mode: screen; pointer-events: none; transition: left 1.4s cubic-bezier(.45,.05,.2,1), top 1.4s cubic-bezier(.45,.05,.2,1), background 1.2s; }
.phm-cork { position: absolute; }
.phm .phm-pin.phm-pin { position: absolute; width: 38%; margin: 0 !important; padding: 5% 5% 15% !important; background: #F5F0E6; border: 0 !important; border-radius: 1px !important; box-shadow: 0 3px 7px rgba(0,0,0,.42) !important; transform: rotate(var(--phm-r)) !important; transition: transform .35s ease, box-shadow .35s; cursor: zoom-in; text-shadow: none !important; min-width: 0 !important; min-height: 0 !important; line-height: 0; font-size: 0; }
.phm .phm-pin.phm-pin::before { content: ''; position: absolute; top: -5%; left: 50%; width: 15%; aspect-ratio: 1; border-radius: 50%; background: radial-gradient(circle at 35% 35%, #ff9a86, #b8322a 60%, #6d1a14); transform: translateX(-50%); box-shadow: 0 1px 2px rgba(0,0,0,.5); z-index: 2; }
.phm .phm-pin.phm-pin:hover, .phm .phm-pin.phm-pin:focus-visible { transform: translateX(-38%) rotate(0deg) scale(2.3) !important; z-index: 20; box-shadow: 0 10px 26px rgba(0,0,0,.55) !important; outline: none; }
.phm-pic { display: block; aspect-ratio: 1; background: #e9e2d3 center / cover no-repeat; }
.phm-note .phm-pic { background: #FBF3DC repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 17%, rgba(90,70,60,.28) 17% 20%); }
.phm-note.phm-lilac .phm-pic { background-color: #ECE4FF; }
.phm .phm-pin.phm-pin.phm-quote { width: 44%; padding: 3.5% 4.5% !important; background: #FFFDF6; }
.phm .phm-pin.phm-pin.phm-quote::before { background: radial-gradient(circle at 35% 35%, #9bb8e6, #1d3f6e 60%, #0d2240); }
.phm-quote .phm-pic { aspect-ratio: 3 / 1; background-color: #FFFDF6; background-image: linear-gradient(rgba(45,42,74,.55), rgba(45,42,74,.55)), linear-gradient(rgba(45,42,74,.42), rgba(45,42,74,.42)); background-size: 80% 1.5px, 54% 1.5px; background-position: 8% 34%, 8% 70%; background-repeat: no-repeat; }
/* ⭐ wave 3 (ruling 32): the favorite of the week, a public keepsake, hangs as a starred slip (a gold pin head) */
.phm .phm-pin.phm-pin.phm-fav { width: 40%; padding: 4% 4.5% !important; background: #FFF8E6; }
.phm .phm-pin.phm-pin.phm-fav::before { background: radial-gradient(circle at 35% 35%, #ffe7a3, #c9922a 60%, #6d4a10); }
.phm-fav .phm-pic { aspect-ratio: 5 / 3; background-color: #FFF8E6; background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23e2b04a' d='M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z'/%3E%3C/svg%3E"), linear-gradient(rgba(74,60,40,.45), rgba(74,60,40,.45)), linear-gradient(rgba(74,60,40,.35), rgba(74,60,40,.35)); background-size: 26% auto, 52% 1.5px, 40% 1.5px; background-position: 8% 50%, 92% 36%, 92% 66%; background-repeat: no-repeat; }
.phm .phm-pin.phm-fresh { animation: phmPinIn .9s cubic-bezier(.2,.8,.2,1.15); }
/* the individual translate/scale/rotate properties compose with the armored transform (an animation can't beat an !important transform) */
@keyframes phmPinIn { from { opacity: 0; translate: 0 -50%; rotate: -18deg; scale: 1.6; } to { opacity: 1; translate: 0 0; rotate: 0deg; scale: 1; } }
.phm .phm-nb.phm-nb { position: absolute; transform: translate(-50%,-50%) !important; z-index: 6; width: 2.4%; min-width: 22px; aspect-ratio: 1; margin: 0; padding: 0 !important; border-radius: 50% !important; border: 2px solid #FFE1B0 !important; display: grid; place-items: center; font-size: 11px; line-height: 1; cursor: pointer; text-shadow: none !important; box-shadow: 0 0 0 3px rgba(255,225,176,.25), 0 4px 10px rgba(0,0,0,.5) !important; animation: phmNb 2.6s ease-in-out infinite; }
.phm .phm-nb.phm-nb:focus-visible, .phm .phm-med.phm-med:focus-visible { outline: 2px solid #FFE1B0; outline-offset: 3px; }
@keyframes phmNb { 0%, 100% { box-shadow: 0 0 0 3px rgba(255,225,176,.25), 0 4px 10px rgba(0,0,0,.5); } 50% { box-shadow: 0 0 0 8px rgba(255,225,176,.06), 0 4px 10px rgba(0,0,0,.5); } }
.phm-here { position: absolute; aspect-ratio: 1; transform: translate(-50%,-50%); z-index: 6; transition: left 1.4s cubic-bezier(.45,.05,.2,1), top 1.4s cubic-bezier(.45,.05,.2,1); }
.phm .phm-med.phm-med { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; padding: 0 !important; border-radius: 50% !important; overflow: hidden; border: 2px solid rgba(255,230,190,.85) !important; cursor: pointer; text-shadow: none !important; transform: none !important; min-width: 0; min-height: 0; }
.phm-med img { width: 100%; height: 100%; object-fit: cover; transform: scale(1.18); display: block; }
.phm-who { position: absolute; top: calc(100% + 5px); left: 50%; transform: translateX(-50%); white-space: nowrap; font: 600 12px/1 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #FFF3E2; text-shadow: 0 1px 3px rgba(0,0,0,.95), 0 0 10px rgba(0,0,0,.7); pointer-events: none; }
.phm-ink { position: absolute; left: 82%; bottom: 84%; padding: 5px 8px; border-radius: 12px 12px 12px 3px; background: #FFF6E8; box-shadow: 0 4px 12px rgba(0,0,0,.35); display: flex; gap: 3px; opacity: 0; transform: translateY(4px) scale(.85); transition: opacity .3s, transform .3s; pointer-events: none; }
.phm-ink.on { opacity: 1; transform: none; }
.phm-ink i { width: 5px; height: 5px; border-radius: 50%; background: #3a2a3f; animation: phmInk 1.1s infinite ease-in-out; }
.phm-ink i:nth-child(2) { animation-delay: .15s; } .phm-ink i:nth-child(3) { animation-delay: .3s; }
@keyframes phmInk { 0%, 80%, 100% { transform: translateY(0); opacity: .45; } 40% { transform: translateY(-3px); opacity: 1; } }
.phm-ink { align-items: center; white-space: nowrap; }
.phm-ink.phm-mirror { left: auto; right: 82%; border-radius: 12px 12px 3px 12px; }
.phm-ink .phm-th { font-size: 14px; line-height: 1; opacity: 0; transform: translateY(5px) scale(.6); transition: opacity .3s, transform .3s; }
.phm-ink.phm-small .phm-th { font-size: 12px; }
.phm-ink .phm-th.phm-in { opacity: 1; transform: none; }
.phm-ink i + .phm-th { margin-left: 3px; }
.phm-sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.phm-whyp { position: absolute; left: 50%; bottom: calc(100% + 14px); transform: translateX(-50%) translateY(4px); width: max-content; max-width: 230px; padding: 8px 11px; border-radius: 10px; background: rgba(20,13,19,.94); border: 1px solid rgba(255,236,210,.18); font: 500 12px/1.35 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: rgba(255,240,222,.92); opacity: 0; pointer-events: none; transition: opacity .25s, transform .25s; z-index: 3; white-space: normal; }
.phm-whyp b { font-weight: 700; }
.phm-whyp.on { opacity: 1; transform: translateX(-50%); }
.phm-floor { position: absolute; left: 0; right: 0; bottom: -1px; height: 12%; pointer-events: none; transition: background 1.1s; z-index: 7; }
.phm-lbx { position: fixed; inset: 0; z-index: 100030; display: grid; place-items: center; padding: 16px; background: rgba(10,6,10,.72); cursor: zoom-out; animation: phmFade .25s ease; }
@keyframes phmFade { from { opacity: 0; } to { opacity: 1; } }
.phm-lbx-leaf { width: min(86vw, 360px); background: #F5F0E6; padding: 12px 12px 14px; box-shadow: 0 18px 40px rgba(0,0,0,.6); transform: rotate(-1.5deg); }
.phm-lbx-leaf img { display: block; width: 100%; max-height: 60vh; object-fit: contain; background: #e9e2d3; }
.phm-lbx-words { font: italic 16px/1.5 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #3a2a2e; padding: 12% 10%; background: #FBF3DC; white-space: pre-wrap; }
.phm-lbx-leaf.phm-lilac .phm-lbx-words { background: #ECE4FF; }
.phm-lbx-quote { font: 500 21px/28px "Caveat", "Bradley Hand", "Segoe Print", cursive; color: #2d2a4a; padding: 10% 9%; background: #FFFDF6 repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 27px, rgba(80,110,160,.18) 27px 28px); white-space: pre-wrap; overflow-wrap: anywhere; }
.phm-lbx-hand { font: 500 21px/28px "Caveat", "Bradley Hand", "Segoe Print", cursive; color: #3a2e1c; padding: 10% 9%; background: #FFF8E6; white-space: pre-wrap; overflow-wrap: anywhere; }
.phm-lbx-cap { font: 600 12px/1.3 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #5a4a4c; margin-top: 10px; }
.phm-rain i { position: absolute; top: -20%; width: 1.5px; border-radius: 1px; background: linear-gradient(to bottom, rgba(210,225,255,0), rgba(210,225,255,.75)); animation: phmRain linear infinite; }
@keyframes phmRain { from { transform: translateY(0); } to { transform: translateY(620%); } }
.phm-glass { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(40,52,78,.35), rgba(40,52,78,.15)); }
.phm-bolt { position: absolute; inset: 0; background: rgba(235,242,255,.95); opacity: 0; pointer-events: none; }
.phm-bolt.phm-go { animation: phmBolt .42s ease-out; }
.phm-roomflash { position: absolute; inset: 0; background: rgba(220,232,255,1); mix-blend-mode: soft-light; opacity: 0; pointer-events: none; }
.phm-roomflash.phm-go { animation: phmFlash .42s ease-out; }
@keyframes phmBolt { 0% { opacity: 0; } 8% { opacity: .95; } 30% { opacity: .15; } 100% { opacity: 0; } }
@keyframes phmFlash { 0% { opacity: 0; } 8% { opacity: .5; } 100% { opacity: 0; } }
.phm-fog { position: absolute; inset: -10%; background: radial-gradient(ellipse at 30% 60%, rgba(220,226,232,.75), rgba(220,226,232,.35) 60%, rgba(220,226,232,.2)); animation: phmFog 40s ease-in-out infinite alternate; }
@keyframes phmFog { from { transform: translateX(-4%); } to { transform: translateX(4%); } }
.phm-snow i { position: absolute; top: -8%; width: 4px; height: 4px; border-radius: 50%; background: rgba(255,255,255,.9); animation: phmSnow linear infinite; }
@keyframes phmSnow { from { transform: translate(0, 0); } to { transform: translate(14px, 1400%); } }
.phm-leaf { position: absolute; top: 20%; left: -12%; font-size: 12px; opacity: 0; animation: phmLeaf 9s linear infinite; }
@keyframes phmLeaf { 0% { opacity: 0; transform: translate(0,0) rotate(0); } 8% { opacity: .9; } 92% { opacity: .9; } 100% { opacity: 0; transform: translate(900%, 180%) rotate(540deg); } }
.phm-heat { position: absolute; inset: -6% 0 0; background: linear-gradient(to bottom, rgba(255,214,150,.26), rgba(255,184,110,.14) 55%, rgba(255,160,90,.06)); mix-blend-mode: screen; pointer-events: none; animation: phmHeat 7s ease-in-out infinite alternate; }
@keyframes phmHeat { from { transform: translateY(0) scaleY(1); opacity: .8; } to { transform: translateY(-2%) scaleY(1.04); opacity: 1; } }
.phm-greet { position: absolute; right: 78%; bottom: 82%; padding: 5px 12px 6px; border-radius: 12px 12px 3px 12px; background: #FFF6E8; color: #2d2a4a; font: 600 clamp(15px, 1.3vw, 19px)/1.15 "Caveat", "Bradley Hand", "Segoe Print", cursive; white-space: nowrap; box-shadow: 0 6px 16px rgba(0,0,0,.4); pointer-events: none; z-index: 4; animation: phmGreet 4.4s ease forwards; }
.phm-greet.phm-mirror { right: auto; left: 78%; border-radius: 12px 12px 12px 3px; }
@keyframes phmGreet { 0% { opacity: 0; transform: translateY(6px) scale(.85); } 14% { opacity: 1; transform: none; } 82% { opacity: 1; } 100% { opacity: 0; transform: translateY(-4px); } }
.phm-dim { position: absolute; inset: 0; pointer-events: none; transition: opacity 2.4s ease; }
.phm-nl { position: absolute; transform: translate(-50%,-50%); width: 9%; height: 22%; border-radius: 50%; background: radial-gradient(closest-side, rgba(255,190,120,.55), rgba(255,170,90,.12) 60%, rgba(255,160,80,0)); mix-blend-mode: screen; pointer-events: none; transition: opacity 2.4s ease; }
.phm .phm-desk.phm-desk { position: absolute; transform: translate(-50%,-50%) rotate(-6deg) !important; z-index: 6; width: 2.6%; min-width: 22px; aspect-ratio: 4 / 5; margin: 0; padding: 0 !important; border-radius: 2px !important; border: 0 !important; background: #FFFDF6 repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 22%, rgba(80,110,160,.35) 22% 26%) !important; box-shadow: 0 0 0 3px rgba(255,225,176,.35), 0 4px 10px rgba(0,0,0,.5) !important; cursor: pointer; text-shadow: none !important; min-height: 0 !important; animation: phmNb 2.6s ease-in-out infinite; }
.phm .phm-desk.phm-desk:focus-visible { outline: 2px solid #FFE1B0; outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) {
  .phm-rain, .phm-snow, .phm-leaf, .phm-bolt, .phm-roomflash { display: none !important; }
  .phm-fog, .phm .phm-desk.phm-desk, .phm-heat, .phm-greet { animation: none !important; }
}
@media (prefers-reduced-motion: reduce) {
  .phm, .phm-box, .phm-here, .phm-pool, .phm-paint, .phm-wash, .phm-win > div, .phm-lamp { transition: none !important; }
  .phm-ink i, .phm-clouds span, .phm-stars i, .phm .phm-nb.phm-nb, .phm .phm-pin.phm-fresh, .phm-lbx { animation: none !important; }
  .phm-ink .phm-th { transition: none !important; }
  .phm .phm-pin.phm-pin { transition: none !important; }
  .phm-stars i { opacity: .75; }
  .phm-meteor { display: none; }
}
/* 💡 E2 (Nina 09-26): the resident's four lights circle the medallion, the room's
   pictures today. A ring 190% of the medallion, one lap in 22 s (the mockup's
   orbit), each light a small cyan mote; a light that goes out drifts off (opacity
   0, scale 2.2: the mockup's .mote.gone). Behind the medallion, the bubble and the
   name (z -1 inside .phm-here's own stacking context), never over the words. */
.phm-pics { position: absolute; left: 50%; top: 50%; width: 190%; aspect-ratio: 1; transform: translate(-50%,-50%); pointer-events: none; z-index: -1; animation: phmOrbit 22s linear infinite; }
@keyframes phmOrbit { to { transform: translate(-50%,-50%) rotate(360deg); } }
.phm-pics-l { position: absolute; inset: 0; }
.phm-pics-l::before { content: ''; position: absolute; left: 50%; top: 0; width: 12%; aspect-ratio: 1; border-radius: 50%; transform: translate(-50%,-50%); background: #EFFFFF; box-shadow: 0 0 8px 3px rgba(111,231,242,.9); transition: opacity 1s ease, transform 1.6s ease; }
.phm-pics-out::before { opacity: 0; transform: translate(-50%,-50%) scale(2.2); }
@media (prefers-reduced-motion: reduce) {
  .phm-pics { animation: none !important; }
  .phm-pics-l::before { transition: none !important; }
}
`;

const mix = (a, b, u) => a.map((v, i) => Math.round(v + (b[i] - v) * u));
const CLOUDS = [
  { width: '34%', height: '14%', top: '16%', animationDuration: '70s' },
  { width: '22%', height: '10%', top: '44%', animationDuration: '95s', animationDelay: '-40s' },
  { width: '28%', height: '12%', top: '58%', animationDuration: '120s', animationDelay: '-80s' },
];

// The real moon, drawn as a terminator (the mockup's moonSVG): lit on the
// right while it waxes, mirrored while it wanes.
function MoonGlyph({ age, box, harvest }) {
  const r = 10;
  const th = (2 * Math.PI * age) / SYNODIC;
  const k = (1 - Math.cos(th)) / 2;
  const rx = Math.abs(Math.cos(th)) * r;
  const wax = age < SYNODIC / 2;
  return (
    <svg className={`phm-moon${harvest ? ' phm-harvest' : ''}`} viewBox="-12 -12 24 24" data-moon={harvest ? 'harvest' : 'moon'}
      style={{ left: `${box.left}%`, top: `${box.top}%`, width: `${box.width}%` }} aria-hidden="true">
      <circle r={r} fill="rgba(255,243,214,.13)" />
      {k < 0.02 ? null : (
        <path d={`M0 ${-r} A${r} ${r} 0 0 1 0 ${r} A${rx.toFixed(2)} ${r} 0 0 ${k > 0.5 ? 1 : 0} 0 ${-r} Z`} fill="#FFF3D6" transform={wax ? undefined : 'scale(-1,1)'} />
      )}
    </svg>
  );
}

export default function PortedHome({
  homeKey,
  minutes = 720,          // the owner's clock, minutes past midnight
  dayKey = null,          // that clock's day: the moon + shower nights
  residentName = null,
  residentImage = null,
  residentStatus = null,
  writing = false,        // ONLY the WS typing pair (§4 honesty)
  returning = false,      // (no `mood` given) a real gap warms the light
  mood = null,            // { key, name, rgb, why } from utils/portedHomes moodState
  pins = null,            // utils/portedHomes boardPins(journal keepsakes): the board
  onOpenJournal = null,   // the owner's 📓 (guests: null, the journal is private)
  isMobile = false,
  still = false,          // prefers-reduced-motion: a still room
  entering = false,       // true while it fades in over the choice scene's last frame
  thinking = null,        // W2c, OWNER ONLY: [{ i, t }] what is going into the reply being written, or null
  weather = null,         // W4: the status `weather` ({ kind, label }) or null (clear)
  nightLight = false,     // W3: the owner waved goodbye: the lamps drop to a night-light
  deskNote = null,        // W3/W5, OWNER ONLY: () => void when a finished task waits on the desk
  greet = false,          // wave 2b: the owner just KNOCKED and walked in: "there you are." by him, in his hand
  pictures = null,        // 💡 E2: the status `pictures` block ({ left, per_day }): his four lights; null = an older backend, no lights
}) {
  const home = homeFor(homeKey);
  const liveKey = home ? home.key : null;
  const boxRef = useRef(null);
  const meteorRef = useRef(null);
  const [size, setSize] = useState({ W: 0, H: 0 });
  const [shown, setShown] = useState(!entering);
  const [resizing, setResizing] = useState(false);
  const [why, setWhy] = useState(false);
  const [open, setOpen] = useState(null);
  const seenPins = useRef(null);
  const boltRef = useRef(null);
  const flashRef = useRef(null);
  const [stormOk, setStormOk] = useState(false);
  // 💭 W2c: up to four icons, each appearing a beat after the last (the
  // mockup's .thought); a still room shows them at once. A refetch that adds
  // an icon never replays the ones already showing.
  const think = useMemo(() => (Array.isArray(thinking) ? thinking.filter((x) => x && x.i).slice(0, 4) : []), [thinking]);
  const thinkKey = think.map((x) => x.i).join('');
  const thinkWords = think.map((x) => String(x.t || '').trim()).filter(Boolean).join(' · ');
  const [thinkShown, setThinkShown] = useState(0);
  useEffect(() => {
    if (!think.length) { setThinkShown(0); return undefined; }
    if (still) { setThinkShown(think.length); return undefined; }
    const timers = think.map((_, k) => setTimeout(() => setThinkShown((n) => Math.max(n, k + 1)), 250 + k * 520));
    return () => timers.forEach(clearTimeout);
  }, [thinkKey, still]); // eslint-disable-line react-hooks/exhaustive-deps

  // the stage takes any box: re-fit on every size change. While the box is
  // changing (the laptop stage easing to a strip) the painting follows it
  // frame by frame; the slow glide is only for his moves between spots.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    let t = null;
    const read = () => {
      const r = el.getBoundingClientRect();
      setSize((s) => {
        if (s.W === r.width && s.H === r.height) return s;
        if (s.W) { setResizing(true); clearTimeout(t); t = setTimeout(() => setResizing(false), 240); }
        return { W: r.width, H: r.height };
      });
    };
    read();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(read); ro.observe(el); } else window.addEventListener('resize', read);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); else window.removeEventListener('resize', read); };
  }, [liveKey]);
  useEffect(() => {
    if (shown) return undefined;
    const t = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(t);
  }, [shown]);
  useEffect(() => {
    if (!why) return undefined;
    const t = setTimeout(() => setWhy(false), 3600);
    return () => clearTimeout(t);
  }, [why]);
  useEffect(() => { if (greet) ensureHandFont(); }, [greet]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const hour = minutes / 60;
  const L = useMemo(() => homeLight(hour), [hour]);
  const moon = useMemo(() => (dayKey ? moonInfo(dayKey) : null), [dayKey]);
  const harvest = !!(moon && moon.full && moon.name === 'the Harvest Moon');
  const shower = dayKey ? showerOn(dayKey) : null;
  const stars = useMemo(() => starField(home), [liveKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const meteorsOn = !!shower && L.n > 0.6 && !still;

  // shooting stars every 2-5 s on a shower night, only while the tab is seen
  useEffect(() => {
    if (!meteorsOn) return undefined;
    let t = null;
    const fire = () => {
      const m = meteorRef.current;
      if (m && !(typeof document !== 'undefined' && document.hidden)) {
        m.classList.remove('phm-go');
        m.style.left = `${50 + Math.random() * 45}%`;
        m.style.top = `${5 + Math.random() * 40}%`;
        void m.offsetWidth; // restart the animation
        m.classList.add('phm-go');
      }
      t = setTimeout(fire, 2200 + Math.random() * 2600);
    };
    t = setTimeout(fire, 500);
    return () => clearTimeout(t);
  }, [meteorsOn]);

  // ⚡ W4: lightning every 5-11 s in a storm, only while the tab is seen. One
  // flash per strike (the ≤ 3/s rule), in the window and a soft wash in the room.
  const wx = useMemo(() => weatherLook(weather, homeLight(minutes / 60)), [weather && weather.kind, minutes]); // eslint-disable-line react-hooks/exhaustive-deps
  const boltsOn = !!(wx && wx.lightning) && !still;
  useEffect(() => {
    if (!boltsOn) return undefined;
    let t = null;
    const strike = () => {
      if (!(typeof document !== 'undefined' && document.hidden)) {
        [boltRef.current, flashRef.current].forEach((el) => {
          if (!el) return;
          el.classList.remove('phm-go'); void el.offsetWidth; el.classList.add('phm-go');
        });
      }
      t = setTimeout(strike, 5000 + Math.random() * 6000);
    };
    t = setTimeout(strike, 1800 + Math.random() * 2500);
    return () => clearTimeout(t);
  }, [boltsOn]);
  const rain = useMemo(() => (wx && (wx.drops || wx.snow) ? rainField(home, wx.snow ? 22 : 34) : []), [wx && wx.drops, wx && wx.snow, liveKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // the newest pin drops in with its pin animation; the board as it loaded
  // (history) is simply there
  const pinIds = (pins || []).map((p) => p.id).join(',');
  // (only a keepsake from the last few minutes: older ones that load later
  // when you scroll back are simply there, never "just pinned")
  const fresh = useMemo(() => {
    const ids = (pins || []).map((p) => p.id);
    if (seenPins.current == null) { seenPins.current = new Set(ids); return new Set(); }
    const nowMs = Date.now();
    const out = new Set((pins || []).filter((p) => !seenPins.current.has(p.id) && p.ts && nowMs - p.ts < 10 * 60 * 1000).map((p) => p.id));
    ids.forEach((id) => seenPins.current.add(id));
    return out;
  }, [pinIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const spot = spotFor(home, residentStatus);
  const pan = coverPan(size.W, size.H, spot);
  const M = mood || { ...MOODS[moodFor({ hour, returning })], key: moodFor({ hour, returning }) };
  if (!home) return null;

  const g = mix(home.ground.day, home.ground.night, L.n);
  const ground = `rgb(${g.join(',')})`;
  const w = home.win;
  const tintOn = home.sky.tint;
  let tint = null;
  if (tintOn && L.dusk > 0 && L.dusk >= L.dawn) tint = { bg: 'linear-gradient(to bottom, #5d4fb0, #e9876a 62%, #ffc27a)', o: L.dusk * 0.9 * (1 - L.n * 0.55) };
  else if (tintOn && L.dawn > 0) tint = { bg: 'linear-gradient(to bottom, #8b9ee0, #f3b0bb 58%, #ffd8a4)', o: L.dawn * 0.85 };
  let wash = null;
  if (wx && wx.drops) wash = { bg: 'rgb(70,90,130)', o: wx.lightning ? 0.35 : 0.22 };   // §6: storm light
  else if (L.dusk > L.dawn && L.dusk > 0) wash = { bg: 'rgb(255,128,64)', o: L.dusk * 0.55 };
  else if (L.dawn > 0) wash = { bg: 'rgb(255,150,170)', o: L.dawn * 0.45 };
  // lamp pools: 0.75 in a storm by day (§6); a night-light after the goodbye
  const lampO = nightLight ? 0 : (wx && wx.drops && L.n < 0.5) ? 0.75 : L.lamps;
  const sky = skyLayers(home, L, wx);
  const mb = moon ? moonBox(home, { harvest }) : null;
  const med = (isMobile ? 0.074 : 0.043) * pan.pw;
  const name = residentName || 'Your resident';
  const c = home.cork;
  const nb = home.notebook;
  const board = pins || [];

  return (
    <div
      ref={boxRef}
      className="phm"
      role="group"
      aria-label={`${name}'s home: ${home.name}. ${name} is at ${spot ? spot.place : 'home'}.`}
      data-home={home.key}
      style={{ backgroundColor: ground, opacity: shown ? 1 : 0 }}
    >
      <style>{CSS}</style>
      {thinkWords ? <div className="phm-sr" role="status">{`On ${name}'s mind: ${thinkWords}`}</div> : null}
      {pan.pw > 0 ? (
        <div className={`phm-box${resizing ? '' : ' phm-glide'}`} style={{ width: pan.pw, height: pan.ph, transform: `translate(${pan.tx}px, ${pan.ty}px)` }}>
          <img className="phm-paint" src={home.art.day} alt="" draggable="false"
            style={wx && wx.stormArt && !stormOk ? { filter: STORM_STAND_IN } : undefined} />
          {home.art.storm && wx && wx.drops ? (
            <img className="phm-paint" src={home.art.storm} alt="" draggable="false" data-storm-art={stormOk ? 'painted' : 'loading'}
              onLoad={() => setStormOk(true)} onError={() => setStormOk(false)}
              style={{ opacity: wx.stormArt && stormOk ? 1 : 0 }} />
          ) : null}
          <img className="phm-paint" src={home.art.night} alt="" draggable="false" style={{ opacity: L.n }} />
          <div className="phm-win" data-sky={L.n > 0.5 ? 'night' : 'day'} style={{ left: `${w.x}%`, top: `${w.y}%`, width: `${w.w}%`, height: `${w.h}%`, borderRadius: w.radius }}>
            <div className="phm-tint" style={{ background: tint ? tint.bg : 'none', opacity: tint ? tint.o : 0 }} />
            {home.sky.clouds ? (
              <div className="phm-clouds" style={{ opacity: sky.clouds }}>
                {CLOUDS.map((s, i) => <span key={i} style={still ? { width: s.width, height: s.height, top: s.top, left: `${12 + i * 26}%` } : s} />)}
              </div>
            ) : null}
            {home.sky.stars ? (
              <div className="phm-stars" style={{ opacity: sky.stars }}>
                {stars.map((s, i) => <i key={i} style={{ left: `${s.left.toFixed(1)}%`, top: `${s.top.toFixed(1)}%`, animationDelay: `${s.delay.toFixed(2)}s` }} />)}
              </div>
            ) : null}
            {mb ? <div className="phm-moonwrap" style={{ opacity: sky.moon }}><MoonGlyph age={moon.age} box={mb} harvest={harvest} /></div> : null}
            {meteorsOn && !(wx && wx.overcast) ? <div className="phm-meteors" data-shower={shower}><div ref={meteorRef} className="phm-meteor" /></div> : null}
            {wx ? (
              <div className="phm-wx" data-weather={wx.kind}>
                {wx.drops ? <div className="phm-glass" /> : null}
                {wx.drops ? <div className="phm-rain">{rain.map((d, i) => <i key={i} style={{ left: `${d.left.toFixed(1)}%`, height: `${d.len.toFixed(0)}%`, animationDuration: `${d.dur.toFixed(2)}s`, animationDelay: `${d.delay.toFixed(2)}s` }} />)}</div> : null}
                {wx.snow ? <div className="phm-snow">{rain.map((d, i) => <i key={i} style={{ left: `${d.left.toFixed(1)}%`, animationDuration: `${(d.dur * 9).toFixed(1)}s`, animationDelay: `${(d.delay * 9).toFixed(1)}s` }} />)}</div> : null}
                {wx.fog ? <div className="phm-fog" /> : null}
                {wx.heat ? <div className="phm-heat" data-heat="" aria-hidden="true" /> : null}
                {wx.leaves ? [0, 1, 2].map((k) => <span key={k} className="phm-leaf" aria-hidden="true" style={{ top: `${18 + k * 22}%`, animationDelay: `${-k * 3.1}s` }}>🍂</span>) : null}
                {wx.lightning ? <div ref={boltRef} className="phm-bolt" /> : null}
              </div>
            ) : null}
          </div>
          <div className="phm-wash" style={{ backgroundColor: wash ? wash.bg : 'transparent', opacity: wash ? wash.o : 0 }} />
          {home.lamps.map((l, i) => (
            <div key={i} className="phm-lamp" style={{ left: `${l.x}%`, top: `${l.y}%`, width: `${l.w}%`, height: `${l.h}%`, opacity: lampO, transition: nightLight ? 'opacity 2.4s ease' : undefined }} />
          ))}
          {wx && wx.lightning ? <div ref={flashRef} className="phm-roomflash" /> : null}
          {/* 🌙 the room itself dims around the one warm pool by him (the night painting keeps its lamp lit, so the pools alone barely read) */}
          <div className="phm-dim" aria-hidden="true" style={{ opacity: nightLight ? 1 : 0, background: spot ? `radial-gradient(circle at ${spot.x}% ${spot.y}%, rgba(8,6,20,0) 7%, rgba(8,6,20,.42) 24%, rgba(8,6,20,.58) 60%)` : 'rgba(8,6,20,.5)' }} />
          {spot ? <div className="phm-nl" data-night-light={nightLight ? '1' : '0'} style={{ left: `${spot.x}%`, top: `${spot.y + 3}%`, opacity: nightLight ? 1 : 0 }} /> : null}
          {c && board.length ? (
            <div className="phm-cork" data-board={board.length} style={{ left: `${c.x}%`, top: `${c.y}%`, width: `${c.w}%`, height: `${c.h}%`, transform: `rotate(${c.rot}deg)` }}>
              {board.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-prop-ui=""
                  className={`phm-pin${p.kind === 'window' ? ' phm-note phm-lilac' : ''}${p.kind === 'quote' ? ' phm-quote' : ''}${p.kind === 'fav' ? ' phm-fav' : ''}${fresh.has(p.id) ? ' phm-fresh' : ''}`}
                  aria-label={p.board || p.caption || 'a keepsake'}
                  title={p.board || p.caption || ''}
                  onClick={(e) => { e.stopPropagation(); setOpen(p); }}
                  style={{ left: `${p.slot[0]}%`, top: `${p.slot[1]}%`, '--phm-r': `${p.slot[2]}deg`, '--prop-bg': p.kind === 'quote' ? '#FFFDF6' : p.kind === 'fav' ? '#FFF8E6' : '#F5F0E6' }}
                >
                  <span className="phm-pic" style={p.kind === 'photo' && p.url ? { backgroundImage: `url("${String(p.url).replace(/"/g, '%22')}")` } : undefined} />
                </button>
              ))}
            </div>
          ) : null}
          {nb && onOpenJournal ? (
            <button
              type="button"
              data-prop-ui=""
              className="phm-nb"
              aria-label={`Open ${name}'s journal`}
              title={`${name}'s journal`}
              onClick={(e) => { e.stopPropagation(); onOpenJournal(); }}
              style={{ left: `${nb.x}%`, top: `${nb.y}%`, '--prop-bg': '#2a1f2a', background: '#2a1f2a' }}
            >📓</button>
          ) : null}
          {deskNote && home.spots.desk ? (
            <button
              type="button"
              data-prop-ui=""
              className="phm-desk"
              data-desk-note=""
              aria-label="Something on the desk for you"
              title="Something on the desk for you"
              onClick={(e) => { e.stopPropagation(); deskNote(); }}
              style={{ left: `${home.spots.desk.x + 4}%`, top: `${home.spots.desk.y - 3}%`, '--prop-bg': '#FFFDF6' }}
            />
          ) : null}
          {spot ? (
            <>
              <div className="phm-pool" style={{ opacity: nightLight ? 0.45 : 1, left: `${spot.x}%`, top: `${spot.y + 4}%`, background: `radial-gradient(closest-side, rgba(${M.rgb},.5), rgba(${M.rgb},.14) 55%, rgba(${M.rgb},0))` }} />
              <div className="phm-here" style={{ left: `${spot.x}%`, top: `${spot.y}%`, width: med }}>
                <button
                  type="button"
                  data-prop-ui=""
                  className="phm-med"
                  aria-label={`${name}. Why is the light this color?`}
                  onClick={(e) => { e.stopPropagation(); setWhy((v) => !v); }}
                  style={{ '--prop-bg': '#1b3a44', background: '#1b3a44', boxShadow: `0 0 0 3px rgba(${M.rgb},.32), 0 0 22px 5px rgba(${M.rgb},.6), 0 6px 14px rgba(0,0,0,.45)` }}
                >
                  {residentImage ? <img src={residentImage} alt="" draggable="false" /> : null}
                </button>
                <div className="phm-who">{name}</div>
                {greet ? <div className={`phm-greet${spot.x < 30 ? ' phm-mirror' : ''}`} data-place-greet="" role="status">there you are.</div> : null}
                {/* the bubble steps aside while the light's reason is showing
                    (the two sit in the same air above him) */}
                <div className={`phm-ink${(writing || think.length) && !why ? ' on' : ''}${spot.x > 70 ? ' phm-mirror' : ''}${isMobile ? ' phm-small' : ''}`} aria-hidden="true" data-thinking={think.length || undefined}>
                  {writing || !think.length ? <><i /><i /><i /></> : null}
                  {think.map((x, k) => <span key={`${k}:${x.i}`} className={`phm-th${k < thinkShown ? ' phm-in' : ''}`}>{x.i}</span>)}
                </div>
                <div className={`phm-whyp${why ? ' on' : ''}`} role="status" data-why={why ? '1' : '0'}>
                  <b style={{ color: `rgb(${M.rgb})` }}>{M.name}</b>{M.why ? ` · ${M.why}` : ''}
                </div>
                {/* 💡 E2: his lights, the room's pictures today (utils/portedPictures);
                    one drifts off per picture, all four come back at the owner's midnight */}
                {(() => {
                  const l = lightsFor(pictures);
                  if (!l) return null;
                  return (
                    <div className="phm-pics" role="img" aria-label={lightsLabel(pictures)} data-pictures-left={l.left} data-pictures-of={l.perDay}>
                      {Array.from({ length: l.perDay }, (_, i) => (
                        <i key={i} className={`phm-pics-l${i < l.left ? '' : ' phm-pics-out'}`} style={{ transform: `rotate(${Math.round((i / l.perDay) * 360)}deg)` }} />
                      ))}
                    </div>
                  );
                })()}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      <div className="phm-floor" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0), ${ground})` }} />
      {open && typeof document !== 'undefined' ? createPortal(
        <div className="phm-lbx" role="dialog" aria-label={open.board || open.caption || 'a keepsake'} onClick={() => setOpen(null)}>
          <div className={`phm-lbx-leaf${open.kind === 'window' ? ' phm-lilac' : ''}`}>
            {open.kind === 'photo' && open.url
              ? <img src={open.url} alt="" />
              : open.kind === 'quote'
                ? <div className="phm-lbx-quote">{open.text}</div>
                : open.kind === 'fav'
                  ? <div className="phm-lbx-hand">{open.text}</div>
                  : <div className="phm-lbx-words">{[open.title, open.body].filter(Boolean).join('\n')}</div>}
            <div className="phm-lbx-cap">{open.board || open.caption}</div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
